import { Info } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { EVENTO_LABELS, UNIT_LABELS } from '@/lib/deadlines'
import { formatDateBr } from '@/lib/publication-inbox'
import type { CalculationMemory, MemoryLine } from '@/services/deadline-service'

const MUTED_EVENTS = new Set<MemoryLine['evento']>(['CONTADO'])

/** Memória do cálculo (cada data relevante, as puladas com o motivo e a fonte). */
export function CalculationMemoryPopover({ memoria }: { memoria: CalculationMemory }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs">
          <Info className="size-3.5" aria-hidden /> Memória do cálculo
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-h-[70vh] w-[26rem] overflow-y-auto text-xs">
        <p className="font-medium">
          {memoria.dias} {UNIT_LABELS[memoria.unidade]} · {memoria.tribunal}{memoria.uf ? `/${memoria.uf}` : ''}
        </p>
        {memoria.avisos.length > 0 && (
          <ul className="space-y-1 rounded-md border border-destructive/30 bg-destructive/5 p-2 text-foreground" aria-label="Avisos do cálculo">
            {memoria.avisos.map((aviso) => <li key={aviso}>{aviso}</li>)}
          </ul>
        )}
        <ol className="space-y-1" aria-label="Linha do tempo do prazo">
          {memoria.linhaDoTempo.map((line, index) => (
            <li key={`${line.data}-${line.evento}-${index}`} className={MUTED_EVENTS.has(line.evento) ? 'text-muted-foreground' : ''}>
              <span className="tabular-nums">{formatDateBr(line.data)}</span>{' '}
              <span className="text-muted-foreground">({line.diaSemana})</span>{' '}
              <strong>{EVENTO_LABELS[line.evento]}{line.contagem ? ` ${line.contagem}º` : ''}</strong>
              {line.evento !== 'CONTADO' && line.motivos.length > 0 && (
                <ul className="ml-3 list-disc pl-3">
                  {line.motivos.map((m, i) => (
                    <li key={i}>
                      {m.descricao}
                      {m.aConferir && <Badge variant="outline" className="ml-1 border-destructive/50 px-1 py-0 text-[10px] text-destructive">a conferir</Badge>}
                      {m.fonteUrl && (
                        <a href={m.fonteUrl} target="_blank" rel="noreferrer noopener" className="ml-1 underline">fonte</a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ol>
        <details>
          <summary className="cursor-pointer text-muted-foreground">Fundamentos</summary>
          <ul className="mt-1 list-disc pl-4">{memoria.fundamentos.map((f) => <li key={f}>{f}</li>)}</ul>
        </details>
      </PopoverContent>
    </Popover>
  )
}
